// src/services/accessRequestsService.js
import { repo } from '../dbservice/repo';
import { AccessRequestModel } from '../models/AccessRequestModel';

/* ─────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────── */

const REQUEST_COLLECTIONS = {
  user:  'accessRequests',
  admin: 'adminAccessRequests',
};

const collectionFor = (requestType) =>
  REQUEST_COLLECTIONS[requestType] || REQUEST_COLLECTIONS.user;

/* ─────────────────────────────────────────────────────────────────────────
   READ
───────────────────────────────────────────────────────────────────────── */

/**
 * Fetch pending access requests via repo.
 * Shape returned is unchanged from the original service.
 */
export const fetchAccessRequests = async (role, companyId, company) => {
  if (role === 'superadmin') {
    const userRequests = await repo.fetchData('accessRequests', [
      { field: 'status', value: 'pending' },
    ]);
    const adminRequests = await repo.fetchData('adminAccessRequests', [
      { field: 'status', value: 'pending' },
    ]);
    return { userRequests, adminRequests };
  }

  if (role === 'admin' || role === 'locationAdmin') {
    if (!companyId && !company) {
      throw new Error('Missing company information for admin fetch');
    }

    let requests = [];

    // Strategy 1: by companyId
    if (companyId) {
      requests = await repo.fetchData('accessRequests', [
        { field: 'status',    value: 'pending'  },
        { field: 'companyId', value: companyId  },
      ]);
    }

    // Strategy 2: by company name if no results
    if (requests.length === 0 && company) {
      const byName = await repo.fetchData('accessRequests', [
        { field: 'status',  value: 'pending' },
        { field: 'company', value: company   },
      ]);
      const existingIds = new Set(requests.map(r => r.id));
      requests = [
        ...requests,
        ...byName.filter(r => !existingIds.has(r.id)),
      ];
    }

    return { userRequests: requests, adminRequests: [] };
  }

  throw new Error('Access denied: Admin or Super Admin only');
};

/* ─────────────────────────────────────────────────────────────────────────
   WRITE — routed through AccessRequestModel
───────────────────────────────────────────────────────────────────────── */

/**
 * Update request status via repo — validated through model.
 * Same signature as the original; callers unchanged.
 */
export const updateRequestStatus = async (
  requestType,
  requestId,
  status,
  extras = {},
  performedBy = null,
) => {
  if (!requestId) throw new Error('requestId is required');

  const collection = collectionFor(requestType);

  const nowIso = new Date().toISOString();

  const finalData = {
    id:          requestId,
    requestType: requestType || 'user',
    status,
    processedAt: nowIso,
    ...extras,
    metadata: {
      ...(extras.metadata || {}),
      updatedAt: nowIso,
      updatedBy: extras.updatedBy || performedBy || 'system',
    },
  };

  const instance = new AccessRequestModel(finalData, { isCreation: false });

  if (!instance.isValid({ partial: true })) {
    throw new Error(
      `Invalid access-request update payload: status must be one of [${AccessRequestModel.VALID_STATUSES.join(', ')}] and requestType one of [${AccessRequestModel.VALID_TYPES.join(', ')}].`,
    );
  }

  const payload = instance.toPayload({ partial: true });

  return await repo.upsertData(collection, requestId, payload);
};

/* ─────────────────────────────────────────────────────────────────────────
   USER LOOKUP
───────────────────────────────────────────────────────────────────────── */

/**
 * Check if email already exists in users collection.
 */
export const checkEmailExists = async (email) => {
  const users = await repo.fetchData('users', [
    { field: 'email', value: email },
  ]);
  return users.length > 0;
};

/* ─────────────────────────────────────────────────────────────────────────
   USER DOCUMENT
───────────────────────────────────────────────────────────────────────── */

/**
 * Create user document via repo — unchanged shape.
 */
export const createUserDocument = async (uid, userDoc) => {
  return await repo.upsertData('users', uid, userDoc);
};

/* ─────────────────────────────────────────────────────────────────────────
   COMPANY DOCUMENT
───────────────────────────────────────────────────────────────────────── */

/**
 * Create company document via repo — unchanged shape.
 */
export const createCompanyDocument = async (companyId, companyData) => {
  return await repo.upsertData('companies', companyId, companyData);
};

/**
 * Fetch company by companyId field.
 */
export const fetchCompanyByCompanyId = async (companyId) => {
  const results = await repo.fetchData('companies', [
    { field: 'companyId', value: companyId },
  ]);
  return results.length > 0 ? results[0] : null;
};

/**
 * Update company document via repo.
 */
export const updateCompanyDocument = async (docId, updateData) => {
  return await repo.upsertData('companies', docId, updateData);
};

/* ─────────────────────────────────────────────────────────────────────────
   AUDIT
───────────────────────────────────────────────────────────────────────── */

/**
 * Log audit trail via repo 
 */
export const logAuditEntry = async (
  action,
  performedBy,
  performedByEmail,
  data,
  extra = {},
) => {
  const auditId = `audit_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return await repo.upsertData('auditLogs', auditId, {
    action,
    performedBy,
    performedByEmail,
    performedAt:     new Date().toISOString(),
    originalRequest: data,
    ...extra,
  });
};